import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    q: 'What ages do you care for at Funtastic Kidz?',
    a: 'We care for children from 6 weeks to 5 years old. Our programs are divided into Nursery (6 weeks to 2 years), Toddlers (2-3 years), and Pre-Kinder & Kindergarten (3-5 years), each designed for specific developmental stages.'
  }, {
    q: 'Are you CCS approved?',
    a: 'Yes, Funtastic Kidz is a fully approved Child Care Subsidy (CCS) provider. Eligible families can claim the subsidy to reduce their daily out-of-pocket costs. We can help you understand how to apply through Services Australia.'
  }, {
    q: 'What is the 3 Day Guarantee and do I qualify?',
    a: 'From 5 January 2026, the 3 Day Guarantee automatically provides 72 subsidised hours per fortnight (3 days a week) to every CCS-eligible family with a household income under $530,000, regardless of activity test results.'
  }, {
    q: 'What are your daily fees?',
    a: 'Our daily fee is $118. This is significantly lower than the Wyndham Vale suburb average of $144. With the Child Care Subsidy applied, many families pay from around $24 to $35 per day out-of-pocket.'
  }, {
    q: 'What are your opening hours?',
    a: 'We are open Monday to Friday from 6:30am to 6:30pm. We are closed on weekends and Victorian public holidays.'
  }, {
    q: 'What meals do you provide?',
    a: 'We provide five fresh, nutritious meals daily prepared on-site: breakfast, morning tea, lunch, afternoon tea, and an evening snack. We cater to dietary, cultural, and religious requirements.'
  }, {
    q: 'What does my child need to bring?',
    a: 'Please bring formula if required, a labelled water bottle, a labelled sunhat, a change of clothes, and a dummy or comforter if your child uses one. We provide nappies, blankets for rest, and all meals.'
  }, {
    q: 'Can I visit my child during the day?',
    a: 'Yes, parents are always welcome to visit their child throughout the day. We have an open-door policy for enrolled families.'
  }, {
    q: 'What if my child is unwell?',
    a: 'If your child becomes unwell during the day, we will contact you immediately to collect them. Our illness policy aligns with the Victorian Department of Health guidelines to protect all children and staff.'
  }, {
    q: 'Do children need to be immunised?',
    a: "Yes, under the 'No Jab, No Play' legislation, children must have an up-to-date immunisation certificate or valid medical exemption documentation to enrol and attend."
  }, {
    q: 'How do I book a tour?',
    a: 'You can book a tour directly through our website using the booking form, or by calling us on 03 8714 3350. We offer tours on Tuesdays, Wednesdays, and Thursdays.'
  }, {
    q: 'How do I enrol?',
    a: "After your tour, if you decide to proceed, we will provide you with an enrolment pack. You'll need to complete the forms, provide your child's birth certificate, immunisation history, and your CRN details for CCS."
  }, {
    q: "What's your NQS rating?",
    a: "Funtastic Kidz is rated as 'Meeting National Quality Standard' (NQS) by ACECQA. Our last regulatory assessment was in September 2025."
  }, {
    q: 'How is Funtastic Kidz different from Goodstart or Busy Bees?',
    a: 'Funtastic Kidz is a family-run, owner-operated centre, not a national chain. This means lower overheads, allowing us to charge $118/day compared to chain rates of $158-$176/day, while maintaining high-quality care and personal relationships.'
  }, {
    q: 'Where is Funtastic Kidz exactly?',
    a: 'We are located at 4 Brightfield Street, Wyndham Vale VIC 3024. We are conveniently situated for families in Wyndham Vale, Manor Lakes, and surrounding suburbs.'
  }];
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
    if (openIndex !== index) {
      console.log('Track: faq_open');
    }
  };
  return <section id="faqs" className="py-20 md:py-32 px-6 bg-white relative overflow-hidden" data-id="element-110">
      {/* Playful blobs */}
      <div className="absolute top-1/3 -left-32 w-96 h-96 bg-yellow/5 rounded-full blur-3xl pointer-events-none" data-id="element-111"></div>
      <div className="absolute bottom-1/3 -right-32 w-96 h-96 bg-blue/5 rounded-full blur-3xl pointer-events-none" data-id="element-112"></div>

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-113">
        <div className="text-center mb-16" data-id="element-114">
          <h2 className="text-3xl md:text-5xl font-extrabold text-navy mb-6 tracking-tight" data-id="element-115">
            Frequently asked questions.
          </h2>
        </div>

        <div className="space-y-4" data-id="element-116">
          {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return <div key={index} className={`rounded-[20px] border-2 overflow-hidden transition-all duration-300 ${isOpen ? 'border-blue bg-blue shadow-md' : 'border-sand bg-white hover:border-blue/30'}`} data-id="element-117">
                <button onClick={() => handleToggle(index)} className="w-full text-left px-6 py-5 md:px-8 md:py-6 flex items-center justify-between focus:outline-none" aria-expanded={isOpen} data-id="element-118">
                  <span className={`font-extrabold text-lg pr-4 transition-colors ${isOpen ? 'text-white' : 'text-navy'}`} data-id="element-119">
                    {faq.q}
                  </span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${isOpen ? 'bg-white/20 text-white' : 'bg-sand/50 text-blue'}`} data-id="element-120">
                    <ChevronDown size={20} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-121" />
                  </div>
                </button>

                <AnimatePresence data-id="element-122">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-123">
                      <div className="px-6 pb-6 md:px-8 md:pb-8" data-id="element-124">
                        <p className="text-white/90 font-medium leading-relaxed pt-4 border-t border-white/20" data-id="element-125">
                          {faq.a}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}